import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useOrganization } from "../../App";
import { leadService } from "@/services/leadService";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SlotBookingFormProps {
  onSubmit: (data: any) => void;
  initialData?: any;
}

export function SlotBookingForm({
  onSubmit,
  initialData,
}: SlotBookingFormProps) {
  const [formData, setFormData] = useState({
    lead_id: initialData?.lead_id || "",
    name: initialData?.name || "",
    email: initialData?.email || "",
    phone: initialData?.phone || "",
    notes: initialData?.notes || "",
    status: initialData?.status || "confirmed",
  });

  const [leads, setLeads] = useState<any[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { currentOrganization } = useOrganization();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLeadSearch = async (query: string) => {
    if (!currentOrganization?.id || query.length < 2) return;

    setSearchQuery(query);
    setLoadingLeads(true);

    try {
      const { data } = await leadService.getLeadsWithPagination({
        organizationId: currentOrganization.id,
        searchQuery: query,
      });

      setLeads(data || []);
    } catch (error) {
      console.error("Error searching leads:", error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleLeadSelect = (leadId: string) => {
    const selectedLead = leads.find((lead) => lead.id === leadId);
    if (selectedLead) {
      setFormData({
        lead_id: selectedLead.id,
        name: `${selectedLead.first_name} ${selectedLead.last_name}`,
        email: selectedLead.email || "",
        phone: selectedLead.phone_number || "",
        notes: formData.notes,
        status: formData.status,
      });
    }
  };

  const handleStatusChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Search Lead (Optional)</Label>
        <div className="relative">
          <Input
            placeholder="Search by name, email, or phone"
            value={searchQuery}
            onChange={(e) => handleLeadSearch(e.target.value)}
          />
          {loadingLeads && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        {leads.length > 0 && searchQuery.length >= 2 && (
          <div className="border rounded-md max-h-40 overflow-y-auto">
            {leads.map((lead) => (
              <div
                key={lead.id}
                className="p-2 hover:bg-accent cursor-pointer"
                onClick={() => handleLeadSelect(lead.id)}
              >
                <div className="font-medium">
                  {lead.first_name} {lead.last_name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {lead.email} {lead.phone_number && `• ${lead.phone_number}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Enter name"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Enter phone number"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select value={formData.status} onValueChange={handleStatusChange}>
          <SelectTrigger id="status">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="waitlisted">Waitlisted</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          placeholder="Enter any notes"
          rows={3}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit">Add Booking</Button>
      </div>
    </form>
  );
}
